import type { SearchProvider, SearchResult, SearchResultType } from "./types";
import { isEmpty, isNil } from "lodash-es";
import logger from "../../utils/logger";

const RECENTLY_SEARCHED_MAX_SIZE = 3;

export type RecentlySearchedStore = {
  get(): string[];
  set(searches: string[]): void;
};

export class GlobalSearchService {
  private readonly providers: SearchProvider[] = [];

  constructor(
    private readonly recentlySearched: RecentlySearchedStore,
    ...providers: SearchProvider[]
  ) {
    if (isEmpty(providers)) {
      throw new Error("At least one search provider is needed");
    }

    this.providers = providers;
  }

  async search(query: string): Promise<SearchResult[]> {
    const result = await Promise.allSettled(this.providers.map((provider) => provider.search(query)));

    if (result.every(({ status }) => status === "rejected")) {
      throw new Error("All search providers failed");
    }

    return result
      .filter((result): result is PromiseFulfilledResult<SearchResult[]> => {
        if (result.status === "rejected") {
          logger.error("Search provider failed with an error", result.reason);
        }

        return result.status === "fulfilled";
      })
      .flatMap(({ value }) => value);
  }

  getProviderByType(type: SearchResultType): SearchProvider {
    const provider = this.providers.find((p) => p.type === type);
    if (isNil(provider)) {
      // This would be really strange, since the given provider did return results
      throw new Error(`Provider with type ${type} not found`);
    }

    return provider;
  }

  addRecentlySearched(query: string): void {
    if (isEmpty(query.trim())) {
      // Do not store blank searches
      return;
    }

    const searches = this.recentlySearched.get().filter((existing) => existing !== query);
    searches.unshift(query);

    if (searches.length > RECENTLY_SEARCHED_MAX_SIZE) {
      searches.pop();
    }

    this.recentlySearched.set(searches);
  }

  getRecentlySearched(): string[] {
    return this.recentlySearched.get();
  }
}
